import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Spin, Typography } from 'components';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import styles from './styles.module.scss';

// Configure PDF.js worker - using CDN with version matching
// This ensures the worker version matches the library version
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  url: string;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ url }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfBlob, setPdfBlob] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch PDF with authentication
  useEffect(() => {
    let blobUrl: string | null = null;

    const fetchPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/pdf',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        blobUrl = URL.createObjectURL(blob);
        setPdfBlob(blobUrl);
      } catch (err: any) {
        setError(err.message || 'Failed to load PDF');
        setLoading(false);
      }
    };

    fetchPdf();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [url]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setLoading(false);
  }

  function onDocumentLoadError(err: Error) {
    console.error('Error loading PDF:', err);
    setError(err.message || 'Failed to render PDF');
    setLoading(false);
  }

  if (error) {
    return (
      <div className={styles.pdfError}>
        <Typography.Text type="danger">{error}</Typography.Text>
      </div>
    );
  }

  if (!pdfBlob) {
    return (
      <div className={styles.pdfLoading}>
        <Spin size="large" />
        <Typography.Text style={{ marginTop: 16 }}>Loading PDF...</Typography.Text>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {loading && (
        <div className={styles.pdfLoading}>
          <Spin size="large" />
          <Typography.Text style={{ marginTop: 16 }}>Rendering PDF...</Typography.Text>
        </div>
      )}

      <Document
        file={pdfBlob}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={null}
        error={
          <div className={styles.pdfError}>
            <Typography.Text type="danger">Failed to render PDF</Typography.Text>
          </div>
        }
        className={styles.pdfDocument}
      >
        {numPages &&
          Array.from(new Array(numPages), (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={Math.min(window.innerWidth - 48, 600)}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
      </Document>
    </div>
  );
};
